// Cálculo puro del valor de inventario del rodeo a partir de filas + precios.
// Sin React ni side-effects → testeable. Usado por la calculadora pública.
import { getCategoryAvgKg, type CategoryPrices } from './publicPricesSnapshot';

export interface RodeoRow {
  categoria: string;
  cabezas: number;
  peso: number; // kg promedio
}

export interface CategoryTotal {
  categoria: string;
  cabezas: number;
  kg: number;
  precioKg: number;
  valor: number; // ARS
}

export interface InventoryResult {
  totalArs: number;
  totalUsd: number | null; // null si no hay usdRate
  totalCabezas: number;
  totalKg: number;
  byCategory: Record<string, CategoryTotal>;
}

/**
 * Valor de inventario del rodeo.
 * - Por cada fila: cabedas × peso × precio_por_kg(categoría) usando el promedio
 *   del último mes (media entre rangos perkg).
 * - Filas con cabezas/peso ≤ 0 o categoría sin precio se ignoran (no suman).
 * - Si `usdRate > 0`, calcula `totalUsd = totalArs / usdRate`.
 */
export function computeInventory(
  rows: RodeoRow[],
  latest: Record<string, CategoryPrices>,
  usdRate: number | null = null
): InventoryResult {
  const byCategory: Record<string, CategoryTotal> = {};
  let totalArs = 0;
  let totalCabezas = 0;
  let totalKg = 0;

  for (const row of rows) {
    const cabezas = Math.max(0, Number(row.cabezas) || 0);
    const peso = Math.max(0, Number(row.peso) || 0);
    if (cabezas <= 0 || peso <= 0) continue;
    const precioKg = getCategoryAvgKg(latest, row.categoria);
    if (precioKg <= 0) continue; // categoría sin precio → no suma
    const kg = cabezas * peso;
    const valor = kg * precioKg;
    totalArs += valor;
    totalCabezas += cabezas;
    totalKg += kg;
    byCategory[row.categoria] = { categoria: row.categoria, cabezas, kg, precioKg, valor };
  }

  return {
    totalArs,
    totalUsd: usdRate && usdRate > 0 ? totalArs / usdRate : null,
    totalCabezas,
    totalKg,
    byCategory,
  };
}
